<script>
import { useTicker } from '@monogrid/vue-boost/core'
import { LinearFilter, Vector2 } from 'three'
import { defineComponent, inject, toValue, watch } from 'vue'

import { useSmoothPointer } from '@/functions/useSmoothPointer'
import fragmentShader from '@/three/shaders/trail.glsl'
import FBO from '@/three/utils/fbo'
import { threeEnvironmentInjection } from '@/three/WebGLApp.vue'

function createFBO ({ width, height, debug }) {
  return new FBO({
    width,
    height,
    name: 'trail',
    shader: fragmentShader,
    debug,
    uniforms: {
      tVelocity: { value: null },
      uOpacity: { value: 0 },
      uAspect: { value: 0 },
      uPointer: { value: new Vector2() },
      uVelocity: { value: new Vector2() },
      uSize: { value: 0.09999 },
      uTime: { value: 0 }
    },
    rtOptions: {
      minFilter: LinearFilter,
      magFilter: LinearFilter
    }
  })
}

function createVelocityFBO ({ width, height, debug }) {
  return new FBO({
    width,
    height,
    name: 'trail-velocity',
    shader: `precision highp float;
      uniform sampler2D texture;
      uniform vec2 uVelocity;
      void main () {
        vec2 uv = gl_FragCoord.xy / RESOLUTION.xy;
        gl_FragColor = vec4(vec3(1., uVelocity * .25) + texture2D(texture, uv).rgb * 0.975, 1.);
      }
    `,
    debug,
    uniforms: {
      uVelocity: { value: new Vector2() }
    },
    rtOptions: {
      minFilter: LinearFilter,
      magFilter: LinearFilter
    }
  })
}

export default defineComponent({
  name: 'CursorTrail',
  components: {},
  props: {
    width: { type: Number, default: 128 },
    height: { type: Number, default: 128 },
    debug: { type: Boolean, default: false }
  },
  setup (props, { slots, expose }) {
    const environment = inject(threeEnvironmentInjection)
    if (!environment) throw new Error('[CursorTrail] A ThreeEnvironment is not provided')

    const pointer = useSmoothPointer()

    let fbo
    let fboVelocity

    function onTick ({ time }) {
      if (!fbo || !fboVelocity) return
      fboVelocity.uniforms.uVelocity.value.x = pointer.velocityX.value
      fboVelocity.uniforms.uVelocity.value.y = pointer.velocityY.value
      fbo.uniforms.tVelocity.value = fboVelocity.target
      fbo.uniforms.uOpacity.value = pointer.speedNormalized.value
      fbo.uniforms.uSize.value = Math.abs(pointer.speedNormalized.value - 1) * 0.09999
      fbo.uniforms.uTime.value = time
      fbo.uniforms.uPointer.value.set(pointer.normalizedX.value, 1 - pointer.normalizedY.value)

      fboVelocity.onTick(environment.webgl.renderer)
      fbo.onTick(environment.webgl.renderer)
    }

    watch(() => [props.width, props.height, props.debug], ([w, h, _debug]) => {
      fbo?.dispose()
      fbo = createFBO({ width: w, height: h, debug: _debug })
      fboVelocity?.dispose()
      fboVelocity = createVelocityFBO({ width: w, height: h, debug: _debug })
    }, { immediate: true })

    watch([() => toValue(environment.width), () => toValue(environment.height)], ([w, h]) => {
      fbo && (fbo.uniforms.uAspect.value = w / h)
      fboVelocity && (fbo.uniforms.uAspect.value = w / h)
    }, { immediate: true })

    useTicker(onTick)

    expose({
      getTarget: () => fbo.target
    })

    return () => slots.default?.()
  }
})
</script>
